import { useEffect, useState } from "react";
import { loadArticles } from "../data/loadArticles";
import SplitListPreview from "./SplitListPreview";

function sortByDateDesc(items = []) {
  return [...items].sort((a, b) => {
    const dateA = new Date(a?.date || 0).getTime();
    const dateB = new Date(b?.date || 0).getTime();
    return dateB - dateA;
  });
}

function normalizeCourseDescription(description) {
  if (!description) return "";
  if (Array.isArray(description)) return description[0] || "";
  if (typeof description === "string") {
    try {
      const parsed = JSON.parse(description);
      if (Array.isArray(parsed)) return parsed[0] || "";
    } catch {
      return description;
    }
    return description;
  }
  return String(description);
}

function normalizeAssetUrl(value) {
  if (!value) return "";
  const str = String(value).trim();
  if (!str) return "";
  if (/^https?:\/\//i.test(str) || str.startsWith("data:")) return str;
  return str.startsWith("/") ? str : `/${str}`;
}

function extractYoutubeId(url = "") {
  if (!url) return "";
  const match = String(url).match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([^?&/]+)/i
  );
  return match ? match[1] : "";
}

function youtubeThumb(url) {
  const id = extractYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
}

function resolveCoursePreviewImage(course = {}) {
  const explicitImage =
    course.image ||
    course.cover ||
    course.thumbnail ||
    course.previewImage ||
    course.heroImage ||
    course.banner;

  if (explicitImage) return normalizeAssetUrl(explicitImage);

  const videoImage =
    youtubeThumb(course.videoUrl) ||
    youtubeThumb(course.video_url) ||
    youtubeThumb(course.youtubeUrl) ||
    youtubeThumb(course.youtube_url);

  if (videoImage) return videoImage;
  return "/images/b_gray-full.svg";
}

export default function BlogHeroList() {
  const [posts, setPosts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([
      loadArticles(),
      fetch("https://www.denisbitter.de/stage01/api/get_courses.php").then((res) => res.json()),
    ])
      .then(([postsResult, coursesResult]) => {
        if (!mounted) return;

        if (postsResult.status === "fulfilled") {
          setPosts(sortByDateDesc(postsResult.value || []));
        } else {
          console.error("Blog Fetch Error:", postsResult.reason);
        }

        if (
          coursesResult.status === "fulfilled" &&
          coursesResult.value?.success &&
          Array.isArray(coursesResult.value?.data)
        ) {
          setCourses(coursesResult.value.data);
        } else if (coursesResult.status === "rejected") {
          console.error("Course Fetch Error:", coursesResult.reason);
        }
      })
      .catch((err) => {
        console.error("Wissen Fetch Error:", err);
        if (!mounted) return;
        setError("Inhalte konnten nicht geladen werden.");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const items = sortByDateDesc([
    ...posts.map((post) => ({
      id: `post-${post.slug}`,
      title: post.title,
      subtitle: post.subtitle,
      meta: "Blog",
      date: post.date,
      image: post.image,
      href: `/post/${post.slug}`,
      external: false,
    })),
    ...courses.map((course) => ({
      id: `course-${course.slug || course.id}`,
      title: course.title,
      subtitle: normalizeCourseDescription(course.description) || "Praxiskurs",
      meta: "Kurs",
      date: course.date || "",
      image: resolveCoursePreviewImage(course),
      href: `/course/${course.slug || course.id}`,
      external: false,
    })),
  ]);

  return (
    <div className="p-3 sm:p-6 max-w-6xl pt-6 sm:pt-14 pb-8 sm:pb-10 mx-auto">
      <SplitListPreview
        label="Wissen"
        command="ls wissen/"
        loading={loading}
        error={error}
        introKicker="Echtes Wissen"
        introHeadline="Artikel und Kurse aus echter Praxis."
        hideCommand
        showDesktopPreview={false}
        items={items}
      />
    </div>
  );
}
